import * as THREE from 'three/webgpu';
import type { MeasureClosestPointResult } from '../types/measure-distance-types';
import type { MeasureEntityRef } from '../types/measure-types';
import { computeDistanceBetweenPointsMillimeter } from '../math/MeasureNumericKernel';

/** 判定零距离不绘制测量线的容差（毫米）。 */
const MEASURE_REFERENCE_DISTANCE_EPSILON_MM = 0.0001;

/**
 * 点到点参考点策略。
 *
 * 职责：双选距离取两次拾取的世界坐标，不吸附圆心或几何最近点。
 */
export class MeasurePointToPointReferenceStrategy {
    /**
     * 求解两实体的点到点参考点对。
     *
     * @param firstEntity 先选实体。
     * @param secondEntity 后选实体。
     * @param pickHistory 测量点击历史（按时间顺序）。
     * @returns 参考点结果；缺少拾取点时返回 null。
     */
    public resolve(
        firstEntity: MeasureEntityRef,
        secondEntity: MeasureEntityRef,
        pickHistory: readonly MeasureEntityRef[],
    ): MeasureClosestPointResult | null {
        const from = resolveLatestPickPointForEntity(firstEntity.objectId, pickHistory);
        const to = resolveLatestPickPointForEntity(secondEntity.objectId, pickHistory);
        if (!from || !to) {
            return null;
        }

        const distance = computeDistanceBetweenPointsMillimeter(from, to);
        return {
            from: from.clone(),
            to: to.clone(),
            distance,
            shouldRenderMeasureLines: distance > MEASURE_REFERENCE_DISTANCE_EPSILON_MM,
        };
    }
}

/**
 * 从点击历史中解析指定实体最近一次仍有效的拾取点。
 *
 * @param objectId 场景对象 ID。
 * @param pickHistory 点击历史。
 * @returns 世界拾取点；未找到时返回 null。
 */
function resolveLatestPickPointForEntity(
    objectId: string,
    pickHistory: readonly MeasureEntityRef[],
): THREE.Vector3 | null {
    for (let index = pickHistory.length - 1; index >= 0; index -= 1) {
        const entry = pickHistory[index];
        if (entry.objectId !== objectId) {
            continue;
        }
        return entry.worldPickPoint.clone();
    }
    return null;
}
